import type {LabItem} from './mini-lab-mechanics';
import type {LabChemical} from './mini-lab-catalog';
import type {ActivitySolution} from './lab-activity-config';
import {emptyChemicalState,solutionSolutes,moveChemicalVolume,reactChemicalState,reactionCatalog,chemicalAppearance} from './chemical-reactions';

/** Volumes of the solutions introduced, not amounts of chemical species after reaction. */
export function liquidContents(item:LabItem):Record<string,number> {
 if(item.volume<=0)return {};
 return item.contents??(item.solutionId?{[item.solutionId]:item.volume}:{});
}
export const hasLiquid=(item:LabItem)=>item.volume>0&&Object.keys(liquidContents(item)).length>0;
export function sameLiquid(a:LabItem,b:LabItem){
 if(a.chemicalState&&b.chemicalState){const ids=new Set([...Object.keys(a.chemicalState.amountsMol),...Object.keys(b.chemicalState.amountsMol)]);return [...ids].every(id=>Math.abs((a.chemicalState!.amountsMol[id]??0)/a.volume-(b.chemicalState!.amountsMol[id]??0)/b.volume)<1e-14)&&Math.abs(a.chemicalState.unmodelledVolumeMl/a.volume-b.chemicalState.unmodelledVolumeMl/b.volume)<1e-10;}
 const ac=liquidContents(a),bc=liquidContents(b);
 return Object.keys(ac).length===Object.keys(bc).length&&Object.entries(ac).every(([id,v])=>Math.abs(v/a.volume-(bc[id]??0)/b.volume)<1e-8);
}
export function setContents(item:LabItem,contents:Record<string,number>){
 item.contents=Object.fromEntries(Object.entries(contents).filter(([,v])=>v>1e-10));
 const ids=Object.keys(item.contents);
 if(ids.length===1)item.solutionId=ids[0];
 else if(item.kind!=='bottle'||ids.length)delete item.solutionId;
 if(!ids.length)delete item.contents;
}
/** Shared conservative transfer for bottles, burettes and timed pipettes. */
export function transferLiquid(source:LabItem,target:LabItem,requested:number,reactionIds?:string[]){
 // Round only the display. Rounding each aliquot can create liquid or stall tiny flows.
 const remaining=target.capacity-target.volume;
 const tolerance=Number.EPSILON*Math.max(1,source.capacity,target.capacity)*8;
 let amount=Math.min(source.volume,remaining,requested);
 // Absorb only floating-point cancellation at an endpoint; never discard the residue.
 if(source.volume<=requested+tolerance&&source.volume<=remaining)amount=source.volume;
 else if(remaining<=requested+tolerance&&remaining<=source.volume)amount=remaining;
 if(!Number.isFinite(amount)||amount<=0||!hasLiquid(source))return 0;
 if(source.chemicalState&&target.chemicalState){const moved=moveChemicalVolume(source.chemicalState,target.chemicalState,amount,reactionCatalog.filter(r=>reactionIds?.includes(r.id)));source.chemicalState=moved.source;target.chemicalState=moved.target;}
 const from={...liquidContents(source)},to={...liquidContents(target)},entries=Object.entries(from);
 let left=amount;
 entries.forEach(([id,volume],i)=>{
  const portion=i===entries.length-1?left:amount*volume/source.volume;
  from[id]=Math.max(0,volume-portion);to[id]=(to[id]??0)+portion;left-=portion;
 });
 source.volume-=amount;target.volume+=amount;
 if(source.chemicalState)source.chemicalState.volumeMl=source.volume;if(target.chemicalState)target.chemicalState.volumeMl=target.volume;
 setContents(source,from);setContents(target,to);return amount;
}
export function contentDescription(item:LabItem,chemicals:LabChemical[]){
 return Object.entries(liquidContents(item)).map(([id,volume])=>`${chemicals.find(c=>c.id===id)?.name??id} : ${volume.toLocaleString('fr-FR',{maximumFractionDigits:2})} mL`).join(' + ');
}
export function liquidAppearance(item:LabItem,chemicals:LabChemical[]):LabChemical|undefined{
 const a=item.appearance??(item.chemicalState?chemicalAppearance(item.chemicalState):undefined);
 if(a){const pure=item.solutionId?chemicals.find(c=>c.id===item.solutionId):undefined;return pure?{...pure,...(item.kind==='bottle'&&item.concentrationMolL!==undefined&&pure.concentrationVisibility==='shown'?{concentrationMolL:item.concentrationMolL}:{}),color:a.color}:{id:'chemical-state',name:'Mélange',color:a.color,type:a.label};}
 if(item.solutionId)return chemicals.find(c=>c.id===item.solutionId);
 if(hasLiquid(item))return {id:'mixture',name:'Mélange',color:'#b8c4c8',type:'Couleur et réaction non modélisées'};
}

/** Upgrade legacy traces once; subsequent transfers carry actual reacted species. */
export function initializeItemChemistry(item:LabItem,solutions:ActivitySolution[],reactionIds:string[]){
 if(item.chemicalState||item.capacity<=0)return;
 let state=emptyChemicalState(item.volume);
 for(const [id,volume] of Object.entries(liquidContents(item))){
  const solution=solutions.find(s=>s.id===id),solutes=solution?.catalogId?solutionSolutes[solution.catalogId]:undefined;
  const concentration=item.kind==='bottle'?item.concentrationMolL??solution?.concentrationMolL:solution?.concentrationMolL;
  if(!solutes||(Object.keys(solutes).length>0&&concentration===undefined)){state.unmodelledVolumeMl+=volume;continue;}
  for(const [species,factor] of Object.entries(solutes))state.amountsMol[species]=(state.amountsMol[species]??0)+factor*(concentration??0)*volume/1000;
 }
 if(item.volume>0&&!Object.keys(liquidContents(item)).length)state.unmodelledVolumeMl=item.volume;
 item.chemicalState=reactChemicalState(state,reactionCatalog.filter(r=>reactionIds.includes(r.id)));
}
